import { Injectable } from '@angular/core';
import { SessionService } from './session.service';
import moment from 'moment';
import { Utils } from './utils.service';
import { DateFormatService, DateValue } from './date-format.service';

const DATE_FORMAT: string = 'YYYY-MM-DD';

@Injectable({
	providedIn: 'root'
})
export class FiltersService {
	categoriesList: Array<Array<{ id: number, name: string }>> = [];
	filters: any;
	filtersCopy: any;
	intervals: Array<{ translate: string, value: number }> = [
		{ translate: 'DAY', value: 1 },
		{ translate: 'WEEK', value: 7 },
		{ translate: 'MONTH', value: 30 }
	];
	selectedCategoriesIds: Array<number>;

	constructor(
		private sessionService: SessionService,
		private dateFormatService: DateFormatService
	) {
		this.setDefaultFilters();
		this.filtersCopy = Utils.deepCopy(this.filters);
	}

	get DateFormat(): string {
		return DATE_FORMAT;
	}

	get Filters(): any {
		return this.filtersCopy;
	}

	get FormattedAnalyticDate(): string {
		const range = this.normalizeRange(this.filters?.date_from, this.filters?.date_to);
		const from = this.dateFormatService.formatDate(range.from as DateValue, 'date');
		const to = this.dateFormatService.formatDate((range.to ?? range.from) as DateValue, 'date');

		if (from && to) return from === to ? from : `${from} - ${to}`;
		return from || to || '';
	}

	private normalizeRange(fromRaw: unknown, toRaw: unknown): { from: unknown; to: unknown } {
		if (fromRaw && typeof fromRaw === 'object') {
			const obj = fromRaw as Record<string, unknown>;
			if ('start' in obj || 'end' in obj) {
				return { from: obj['start'], to: obj['end'] };
			}
			if ('date_from' in obj || 'date_to' in obj) {
				return { from: obj['date_from'], to: obj['date_to'] };
			}
		}

		return { from: fromRaw, to: toRaw };
	}

	apply(): void {
		if (this.filtersCopy != null) this.filters = Utils.deepCopy(this.filtersCopy);
	}

	cancel(): void {
		this.filtersCopy = Utils.deepCopy(this.filters);
	}

	setDefaultFilters(): void {
		this.filters = {};

		if (this.sessionService.defaults.date_from && this.sessionService.defaults.date_to) {
			this.filters.date = moment(this.sessionService.defaults.date_to).format(DATE_FORMAT);
			this.filters.date_to = moment(this.sessionService.defaults.date_to).format(DATE_FORMAT);

			let date_from: moment.Moment = moment(this.sessionService.defaults.date_to).subtract(29, 'day');

			if (moment(this.sessionService.defaults.date_from).isAfter(date_from)) {
				date_from = moment(this.sessionService.defaults.date_from);
			}

			this.filters.date_from = date_from.format(DATE_FORMAT);
		}

		this.filters.category = this.sessionService.defaults.root_category;
		this.filters.metrics = ['turnover', 'qty', 'receipts_qty', 'profit', 'stock_qty', 'stock_total_price', 'price', 'linear_meter_turnover', 'linear_meter_profit'];
		this.filters.layout_distribution = 'discrete';
		this.filters.layout_color = '#2ECC40';
		this.filters.color_values = {
			maxXmaxY: '#2ECC40',
			minXmaxY: '#FFDC00',
			maxXminY: '#0074D9',
			minXminY: '#FF4136'
		};
		this.filters.percent_dist = [10, 50, 90];
		this.filters.promo_products = false;
		this.filters.new_products = false;
		this.filters.price_products = false;

		this.filters.time_from = '00:00';
		this.filters.time_to = '00:00';

		this.filters.colors = [
			{ a: '#32CD32', b: '#00BFFF', c: '#FF0000' },
			{ a: '#32CD32', b: '#00BFFF', c: '#FF0000' },
			{ a: '#32CD32', b: '#00BFFF', c: '#FF0000' },
			{ a: '#32CD32', b: '#00BFFF', c: '#FF0000' }
		];
		this.filters.percents = [
			{ a: 80, b: 15, c: 5 },
			{ a: 80, b: 15, c: 5 },
			{ a: 80, b: 15, c: 5 },
			{ a: 80, b: 15, c: 5 }
		];
		this.filters.input_errors = [false, false, false, false];
		this.filters.surplus_low_color = '#90EE90';
		this.filters.surplus_high_color = '#32CD32';
		this.filters.remains_low_color = '#CD5C5C';
		this.filters.remains_high_color = '#FF0000';
		this.filters.surplus_border_percent = 50;
		this.filters.remains_border_percent = 50;
		this.filters.interval = 1;
		this.filters.show_blocks = true;
		this.filters.show_articles = false;
		this.filters.transparentBoxes = this.sessionService.personalSettings.transparentBoxesMode;
		this.filters.POSMaterials = true;
		this.filters.equipmentSizes = false;
		this.filters.storeProducts = {
			color: '#2ECC40',
			stand_type: 'all',
			show: 'planogram_space'
		};
	}

	setFilters(filters?: any): void {
		if (!filters) {
			this.setDefaultFilters();
		} else {
			Object.assign(this.filters, filters);
		}
		this.filtersCopy = Utils.deepCopy(this.filters);
	}
}